import {
  useLazyPostDocumentCreateUnassignedQuery,
  useLazyPostDocumentGetUploadUnassignedQuery,
  useLazyPostDocumentUploadCompleteUnassignedQuery
} from '@/api';
import { useAzureFileUpload } from '@repo/file-uploader';
import {
  selectMedicalRecordsUploadingDocuments,
  setMedicalRecordsUploadingDocuments,
  useAppDispatch,
  useAppSelector
} from '@/store';

export const useDragAndDropFilesUpload = () => {
  const dispatch = useAppDispatch();

  const medicalRecordsUploadingDocuments = useAppSelector(selectMedicalRecordsUploadingDocuments);

  const [triggerPostDocumentCreateUnassigned] = useLazyPostDocumentCreateUnassignedQuery();

  const [triggerPostDocumentGetUploadUnassigned] = useLazyPostDocumentGetUploadUnassignedQuery();
  const [triggerPostDocumentUploadCompleteUnassigned] = useLazyPostDocumentUploadCompleteUnassignedQuery();

  const { uploadFile, deleteUpload } = useAzureFileUpload({
    onUploadComplete: (uploadId: string) => {
      const [docId, unassignedDocId] = uploadId.split(':');
      triggerPostDocumentUploadCompleteUnassigned({
        documentId: docId === 'null' ? null : docId,
        unassignedDocumentId: unassignedDocId === 'null' ? null : unassignedDocId
      })
        .unwrap()
        .then(() => {
          deleteUpload(uploadId);
          dispatch(
            setMedicalRecordsUploadingDocuments(
              medicalRecordsUploadingDocuments.filter(
                (document) => `${document.documentId}:${document.unassignedDocumentId}` !== uploadId
              )
            )
          );
        })
        .catch((error) => {
          console.error(`Failed to complete upload for ${uploadId}:`, error);
        });
    }
  });

  const onFilesStartUpload = async (selectedFiles: File[]) => {
    const documentIdList = await Promise.all(
      selectedFiles.map(async (selectedFile: File) => {
        try {
          const data = await triggerPostDocumentCreateUnassigned({
            documentName: selectedFile.name,
            documentSizeMb: +(selectedFile.size / (1024 * 1024))
          }).unwrap();

          return {
            file: selectedFile,
            documentId: data.documentId,
            unassignedDocumentId: data.unassignedDocumentId
          };
        } catch (error) {
          return {
            file: selectedFile,
            documentId: null,
            unassignedDocumentId: null,
            error: error instanceof Error ? error.message : String(error)
          };
        }
      })
    );

    const uploadFileInfos = await Promise.all(
      // Only process successful uploads
      documentIdList.map(async ({ documentId, unassignedDocumentId, file }) => {
        if (
          (unassignedDocumentId && typeof unassignedDocumentId === 'string') ||
          (documentId && typeof documentId === 'string')
        ) {
          const data = await triggerPostDocumentGetUploadUnassigned({
            documentId: documentId,
            unassignedDocumentId: unassignedDocumentId
          }).unwrap();

          return {
            file: file,
            documentId: documentId,
            unassignedDocumentId: unassignedDocumentId,
            uploadPath: data.uploadPath,
            uploadToken: data.uploadToken
          };
        } else {
          return {
            file: file,
            documentId: documentId,
            unassignedDocumentId: unassignedDocumentId,
            uploadPath: null,
            uploadToken: null
          };
        }
      })
    );

    const uploadedFileUnassignedDocuments = uploadFileInfos.map(
      ({ file, documentId, unassignedDocumentId, uploadPath, uploadToken }) => {
        const uploadID = `${documentId}:${unassignedDocumentId}`;
        if (uploadID && uploadPath && uploadToken) {
          uploadFile(uploadID, file, uploadPath, uploadToken);
          return {
            documentId: documentId,
            unassignedDocumentId: unassignedDocumentId
          };
        } else {
          return { documentId: null, unassignedDocumentId: null };
        }
      }
    );

    dispatch(
      setMedicalRecordsUploadingDocuments(
        [...medicalRecordsUploadingDocuments, ...uploadedFileUnassignedDocuments].filter(
          (fileInfo) => fileInfo.documentId !== null || fileInfo.unassignedDocumentId !== null
        )
      )
    );
  };

  return {
    onFilesStartUpload
  };
};
